import axios from 'axios';
import { installAdminSessionRefresh } from './adminSessionRefresh';

// Base URL senza /api - verrà aggiunto nei singoli endpoint
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
console.log('🔧 [api.js] API_BASE_URL from env:', API_BASE_URL);

/**
 * Get CSRF token from cookie
 * The token is stored in a non-httpOnly cookie for client-side access
 */
function getCsrfToken() {
  const cookieName = 'csrf-token';
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,  // Aggiungo /api qui
  timeout: 10000,
  withCredentials: true,  // ⬅️ CRITICAL: Invia cookies automaticamente
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('🔧 [api.js] Axios baseURL configured as:', `${API_BASE_URL}/api`);

export function buildFeatureSearchParams(filters = {}) {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value === '') continue;
    params.append(key, String(value));
  }

  return params;
}

// Interceptor per logging delle richieste e aggiunta CSRF token
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
    console.log('🍪 Cookies will be sent automatically (withCredentials: true)');

    // Add CSRF token for mutating requests
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
        console.log('🛡️ Added CSRF token to request headers');
      } else {
        console.warn('⚠️ No CSRF token found in cookies');
      }
    }

    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor per logging delle risposte
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);

    return Promise.reject(error);
  }
);

installAdminSessionRefresh(api);

// ==========================================
// TENANT SERVICES (ADMIN ONLY)
// ==========================================

export const tenantService = {
  // Lista tutti i tenants con statistiche (ADMIN endpoint)
  async getTenants() {
    const response = await api.get('/admin/tenants/list-with-stats');
    return response.data;
  },

  // Ottieni dettagli completi tenant (ADMIN endpoint)
  async getTenant(tenantId) {
    const response = await api.get(`/admin/tenants/${tenantId}/details`);
    return response.data;
  },

  async getTenantLegalDocuments(tenantId) {
    const response = await api.get(`/admin/tenants/${tenantId}/legal-documents`);
    return response.data;
  },

  // Cambia status tenant (ADMIN endpoint)
  async updateTenantStatus(tenantId, status, reason = '', suspensionNote = '') {
    const response = await api.post(`/admin/tenants/${tenantId}/change-status`, {
      status,
      reason,
      suspensionNote: suspensionNote || undefined,
    });
    return response.data;
  },

  // Aggiorna alias tenant (ADMIN endpoint)
  async updateTenantAlias(tenantId, adminAlias = '') {
    const response = await api.patch(`/admin/tenants/${tenantId}/alias`, {
      adminAlias
    });
    return response.data;
  },

  // Elimina tenant (ADMIN endpoint)
  async deleteTenant(tenantId, reason = '') {
    const response = await api.delete(`/admin/tenants/${tenantId}`, {
      data: { reason }
    });
    return response.data;
  }
};

// ==========================================
// ACTIVATION TRACKER SERVICES (ADMIN ONLY)
// ==========================================

export const activationTrackerService = {
  async getTenants(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const query = params.toString();
    const response = await api.get(`/admin/activation-tracker/tenants${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getTenant(tenantId) {
    const response = await api.get(`/admin/activation-tracker/tenants/${tenantId}`);
    return response.data;
  },

  async sendContractReminder(tenantId, payload = {}, stepKey = '') {
    const response = await api.post(`/admin/activation-tracker/tenants/${tenantId}/contract-reminder`, {
      ...payload,
      stepKey,
    });
    return response.data;
  },

  async previewContractReminder(tenantId, stepKey = '') {
    const query = stepKey ? `?stepKey=${encodeURIComponent(stepKey)}` : '';
    const response = await api.get(`/admin/activation-tracker/tenants/${tenantId}/contract-reminder-preview${query}`);
    return response.data;
  },

  async sendVenueReminder(tenantId, payload = {}, stepKey = '') {
    const response = await api.post(`/admin/activation-tracker/tenants/${tenantId}/venue-reminder`, {
      ...payload,
      stepKey,
    });
    return response.data;
  },

  async previewVenueReminder(tenantId, stepKey = '') {
    const query = stepKey ? `?stepKey=${encodeURIComponent(stepKey)}` : '';
    const response = await api.get(`/admin/activation-tracker/tenants/${tenantId}/venue-reminder-preview${query}`);
    return response.data;
  },

  async sendDeviceActivationReminder(tenantId, payload = {}, venueId = '', deviceId = '') {
    const response = await api.post(`/admin/activation-tracker/tenants/${tenantId}/device-activation-reminder`, {
      ...payload,
      venueId,
      deviceId,
    });
    return response.data;
  },

  async previewDeviceActivationReminder(tenantId, venueId = '', deviceId = '') {
    const params = new URLSearchParams();
    if (venueId) params.append('venueId', venueId);
    if (deviceId) params.append('deviceId', deviceId);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/admin/activation-tracker/tenants/${tenantId}/device-activation-reminder-preview${query}`);
    return response.data;
  },

  async sendWorkerOnboardingReminder(tenantId, payload = {}, venueId = '', stepKey = '') {
    const response = await api.post(`/admin/activation-tracker/tenants/${tenantId}/worker-onboarding-reminder`, {
      ...payload,
      venueId,
      stepKey,
    });
    return response.data;
  },

  async previewWorkerOnboardingReminder(tenantId, venueId = '', stepKey = '') {
    const params = new URLSearchParams();
    if (venueId) params.append('venueId', venueId);
    if (stepKey) params.append('stepKey', stepKey);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/admin/activation-tracker/tenants/${tenantId}/worker-onboarding-reminder-preview${query}`);
    return response.data;
  }
};

// ==========================================
// VENUE SERVICES (ADMIN ONLY)
// ==========================================

export const venueService = {
  // Lista tutti i venues con filtri (ADMIN cross-tenant)
  async getVenues(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/admin/venues/all-with-filters?${params}`);
    return response.data;
  },

  // Ottieni dettagli completi venue (ADMIN endpoint)
  async getVenue(venueId) {
    const response = await api.get(`/admin/venues/${venueId}/details`);
    return response.data;
  },

  // Elimina venue (ADMIN endpoint)
  async deleteVenue(venueId, arg = '') {
    const data =
      arg && typeof arg === 'object'
        ? arg
        : { reason: arg };

    const response = await api.delete(`/admin/venues/${venueId}`, {
      data
    });
    return response.data;
  },

  // Anteprima eliminazione venue (ADMIN endpoint)
  async getVenueDeletionPreview(venueId) {
    const response = await api.get(`/admin/venues/${venueId}/deletion-preview`);
    return response.data;
  },

  // Aggiorna informazioni base venue (ADMIN endpoint)
  async updateVenue(venueId, { name, address, venueCategoryId, notifyWs }) {
    const response = await api.put(`/admin/venues/${venueId}`, {
      name,
      address,
      venueCategoryId,
      notifyWs,
    });
    return response.data;
  },

  // Get effective fee config for a venue (ADMIN endpoint)
  async getVenueAdminSettings(venueId) {
    const response = await api.get(`/admin/venues/${venueId}/admin-settings`);
    return response.data;
  },

  // Override fee config for a venue (ADMIN endpoint)
  async updateVenueAdminSettings(venueId, { soontipFeePercent, soontipFeeFixedEur }) {
    const response = await api.put(`/admin/venues/${venueId}/admin-settings`, {
      soontipFeePercent,
      soontipFeeFixedEur
    });
    return response.data;
  },

  // Reset venue override so it falls back to global defaults (or ENV emergency)
  async resetVenueAdminSettings(venueId) {
    const response = await api.delete(`/admin/venues/${venueId}/admin-settings`);
    return response.data;
  },

  // Get InviteConsentAcceptance records grouped by role for all venue users
  async getVenueUserConsents(venueId) {
    const response = await api.get(`/admin/venues/${venueId}/user-consents`);
    return response.data;
  },

  async getVenueDistributionRules(venueId) {
    const response = await api.get(`/admin/venues/${venueId}/distribution-rules`);
    return response.data;
  },

  async saveVenueDistributionRules(venueId, payload) {
    const response = await api.put(`/admin/venues/${venueId}/distribution-rules`, payload);
    return response.data;
  },
};

// ==========================================
// GLOBAL SETTINGS (ADMIN ONLY)
// ==========================================

export const globalSettingsService = {
  async getGlobalSettings() {
    const response = await api.get('/admin/global-settings');
    return response.data;
  },

  async updateGlobalSettings({
    defaultSoontipFeePercent,
    defaultSoontipFeeFixedEur,
    workerMinimumPayoutEur,
    workerInstantPayoutFeePercent,
    workerInstantPayoutFeeFixedEur,
  }) {
    const response = await api.put('/admin/global-settings', {
      defaultSoontipFeePercent,
      defaultSoontipFeeFixedEur,
      workerMinimumPayoutEur,
      workerInstantPayoutFeePercent,
      workerInstantPayoutFeeFixedEur,
    });
    return response.data;
  },

  async getAlertEmails() {
    const response = await api.get('/admin/global-settings/alert-emails');
    return response.data; // { alertEmails: [...] }
  },

  async updateAlertEmails(alertEmails) {
    const response = await api.put('/admin/global-settings/alert-emails', { alertEmails });
    return response.data;
  },

  async getTenantNotificationSettings() {
    const response = await api.get('/admin/global-settings/tenant-notifications');
    return response.data;
  },

  async updateTenantNotificationSettings(recipients, events) {
    const response = await api.put('/admin/global-settings/tenant-notifications', {
      recipients,
      events,
    });
    return response.data;
  },

  async sendTenantNotificationTest(eventType) {
    const response = await api.post('/admin/global-settings/tenant-notifications/test', { eventType });
    return response.data;
  },

  async enableAutoActivateTenant() {
    const response = await api.post('/admin/global-settings/auto-activate-tenant/enable');
    return response.data;
  },

  async disableAutoActivateTenant() {
    const response = await api.post('/admin/global-settings/auto-activate-tenant/disable');
    return response.data;
  }
};

// ==========================================
// SAFETY PAYOUTS (ADMIN ONLY)
// ==========================================

export const safetyPayoutService = {
  async getCandidates({ filter = 'TO_MANAGE', search = '', tenantId = '', venueId = '', page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ filter, page: String(page), pageSize: String(pageSize) });
    if (search.trim()) params.set('search', search.trim());
    if (tenantId) params.set('tenantId', tenantId);
    if (venueId) params.set('venueId', venueId);
    const response = await api.get(`/admin/safety-payouts?${params.toString()}`, { timeout: 120000 });
    return response.data;
  },

  async refresh({ filter = 'TO_MANAGE', search = '', tenantId = '', venueId = '', page = 1, pageSize = 25 } = {}) {
    const response = await api.post('/admin/safety-payouts/refresh', {
      filter,
      search,
      tenantId,
      venueId,
      page,
      pageSize,
    }, { timeout: 120000 });
    return response.data;
  },

  async execute(workerPaymentProfileId, action, requestId) {
    const response = await api.post(`/admin/safety-payouts/${workerPaymentProfileId}/payout`, { action, requestId });
    return response.data;
  },
};

export const workerPayoutService = {
  async getPayouts(filters = {}) {
    const params = buildFeatureSearchParams(filters);
    const response = await api.get(`/admin/worker-payouts?${params.toString()}`);
    return response.data;
  },
};

// ==========================================
// TEAM SERVICES (ADMIN ONLY)
// ==========================================

export const teamService = {
  // Lista teams con filtri (ADMIN cross-tenant)
  async getTeams(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/admin/teams/all-with-filters?${params}`);
    return response.data;
  },

  // Ottieni dettagli completi team (ADMIN endpoint)
  async getTeam(teamId) {
    const response = await api.get(`/admin/teams/${teamId}/details`);
    return response.data;
  },

  // Elimina team (ADMIN endpoint)
  async deleteTeam(teamId, reason = '') {
    const response = await api.delete(`/admin/teams/${teamId}`, {
      data: { reason }
    });
    return response.data;
  }
};

// ==========================================
// WORKER SERVICES (ADMIN ONLY)
// ==========================================

export const workerService = {
  // Vista admin workers con attività mese + costi Stripe Connect persistiti
  async getMonthlyInsights(filters = {}) {
    const params = buildFeatureSearchParams(filters);
    const response = await api.get(`/admin/workers/monthly-insights?${params}`);
    return response.data;
  },

  // Stato sync Stripe Connect cost per mese
  async getConnectCostSyncStatus(filters = {}) {
    const params = buildFeatureSearchParams(filters);
    const response = await api.get(`/admin/workers/connect-cost-sync-status?${params}`);
    return response.data;
  },

  // Export JSON completo per Excel frontend (all filtered rows, same sort, no UI pagination)
  async exportMonthlyInsights(filters = {}) {
    const params = buildFeatureSearchParams(filters);
    const response = await api.get(`/admin/workers/monthly-insights/export?${params}`);
    return response.data;
  },

  // Trigger manuale sync Stripe Connect cost per mese
  async syncConnectCosts(payload = {}) {
    const response = await api.post('/admin/workers/sync-connect-costs', payload, {
      timeout: 150000,
    });
    return response.data;
  },

  // Lista workers con filtri (ADMIN cross-tenant)
  async getWorkers(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/admin/workers/all-with-filters?${params}`);
    return response.data;
  },

  // Get all workers (alias for invoice generation)
  async getAllWorkers() {
    return this.getWorkers({});
  },

  // Ottieni dettagli completi worker (ADMIN endpoint)
  async getWorker(workerId) {
    const response = await api.get(`/admin/workers/${workerId}/full-details`);
    return response.data;
  },

  // Workers con problemi di pagamento (ADMIN endpoint)
  async getWorkersWithPaymentIssues() {
    const response = await api.get('/admin/workers/payment-issues');
    return response.data;
  },

  // Reinvia invito a worker (ADMIN endpoint)
  async resendInvite(workerId) {
    const response = await api.post(`/admin/workers/${workerId}/resend-invite`);
    return response.data;
  },

  // Elimina worker (ADMIN endpoint)
  async deleteWorker(workerId, reason = '') {
    const response = await api.delete(`/admin/workers/${workerId}`, {
      data: { reason }
    });
    return response.data;
  }
};

// ==========================================
// DEVICE SERVICES (ADMIN ONLY)
// ==========================================

export const deviceService = {
  // Lista devices con filtri (ADMIN cross-tenant)
  async getDevices(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/admin/devices/all-with-filters?${params}`);
    return response.data;
  },

  // Ottieni dettagli completi device (ADMIN endpoint)
  async getDevice(deviceId) {
    const response = await api.get(`/admin/devices/${deviceId}/full-details`);
    return response.data;
  },

  // Statistiche devices (ADMIN endpoint)
  async getDeviceStats() {
    const response = await api.get('/admin/devices/stats/overview');
    return response.data;
  },

  // Cambia status device (ADMIN endpoint)
  async updateDeviceStatus(deviceId, status, reason = '') {
    const response = await api.post(`/admin/devices/${deviceId}/change-status`, {
      status,
      reason
    });
    return response.data;
  },

  // Aggiorna device (ADMIN endpoint)
  async updateDevice(deviceId, deviceData) {
    const response = await api.put(`/admin/devices/${deviceId}`, deviceData);
    return response.data;
  },

  // Assegna device a venue (ADMIN endpoint)
  async assignDeviceToVenue(deviceId, venueId) {
    const response = await api.post(`/admin/devices/${deviceId}/assign-to-venue`, {
      venueId
    });
    return response.data;
  },

  // Controlla se device è connesso al WebSocket (ADMIN only)
  async checkDeviceConnection(deviceId) {
    const response = await api.get(`/admin/device-ws/${deviceId}/websocket/status`);
    return response.data;
  },

  // Invia messaggio display a device specifico (ADMIN only)
  async sendDisplayMessage(deviceId, messageData) {
    const response = await api.post(`/admin/device-ws/${deviceId}/display-message`, messageData);
    return response.data;
  },

  // Invia comando WebSocket generico a device specifico (ADMIN only)
  async sendWebSocketCommand(deviceId, commandData) {
    const response = await api.post(`/admin/device-ws/${deviceId}/command`, commandData);
    return response.data;
  }
};



// ==========================================
// DASHBOARD SERVICES (ADMIN ONLY)
// ==========================================

export const dashboardService = {
  // Overview dashboard con KPI (ADMIN endpoint)
  async getOverview() {
    const response = await api.get('/admin/dashboard/overview');
    return response.data;
  },

  // Metriche di crescita (ADMIN endpoint)
  async getGrowthMetrics(period = 30) {
    const response = await api.get(`/admin/dashboard/growth-metrics?period=${period}`);
    return response.data;
  },

  // Health check - issues da risolvere (ADMIN endpoint)
  async getHealthCheck() {
    const response = await api.get('/admin/dashboard/health-check');
    return response.data;
  }
};

// ==========================================
// TIP TRANSACTIONS SERVICES (ADMIN ONLY)
// ==========================================

export const tipTransactionService = {
  // Lista tutte le transazioni con filtri e paginazione
  async getTransactions(filters = {}) {
    const params = new URLSearchParams();

    // Gestisci filtri
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.venueId) params.append('venueId', filters.venueId);
    if (filters.deviceId) params.append('deviceId', filters.deviceId);
    if (filters.status) params.append('status', filters.status);
    if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters.recipientMode) params.append('recipientMode', filters.recipientMode);
    if (filters.distributionMode) params.append('distributionMode', filters.distributionMode);
    if (filters.cardCountry) params.append('cardCountry', filters.cardCountry);
    if (filters.terminalOutcome) params.append('terminalOutcome', filters.terminalOutcome);
    if (filters.collectionChannel) params.append('collectionChannel', filters.collectionChannel);
    if (filters.acubeInvoiceStatus) params.append('acubeInvoiceStatus', filters.acubeInvoiceStatus);
    if (filters.q) params.append('q', filters.q);
    if (filters.page) params.append('page', filters.page);
    if (filters.pageSize) params.append('pageSize', filters.pageSize);
    if (filters.summary) params.append('summary', 'true');
    if (filters.summaryIgnoreStatusFilters) params.append('summaryIgnoreStatusFilters', 'true');

    const response = await api.get(`/admin/tip-transactions?${params}`);
    return response.data;
  },

  // Ottieni dettaglio transazione
  async getTransaction(transactionId) {
    const response = await api.get(`/admin/tip-transactions/${transactionId}`);
    return response.data;
  },

  // Ottieni summary metrics
  async getSummary(filters = {}) {
    const params = new URLSearchParams();

    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.venueId) params.append('venueId', filters.venueId);
    if (filters.deviceId) params.append('deviceId', filters.deviceId);
    if (filters.status) params.append('status', filters.status);
    if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters.recipientMode) params.append('recipientMode', filters.recipientMode);
    if (filters.distributionMode) params.append('distributionMode', filters.distributionMode);
    if (filters.cardCountry) params.append('cardCountry', filters.cardCountry);
    if (filters.terminalOutcome) params.append('terminalOutcome', filters.terminalOutcome);
    if (filters.collectionChannel) params.append('collectionChannel', filters.collectionChannel);
    if (filters.acubeInvoiceStatus) params.append('acubeInvoiceStatus', filters.acubeInvoiceStatus);

    const response = await api.get(`/admin/tip-transactions/summary?${params}`);
    return response.data;
  }
};

// ==========================================
// ANALYTICS SERVICES (ADMIN ONLY)
// Feature-flagged: ENABLE_ADMIN_ANALYTICS
// ==========================================

export const analyticsService = {
  /**
   * Get analytics overview dashboard data
   * @param {Object} filters - { from, to, groupBy }
   */
  async getOverview(filters = {}) {
    const params = new URLSearchParams();

    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.groupBy) params.append('groupBy', filters.groupBy);

    const response = await api.get(`/admin/analytics/overview?${params}`);
    return response.data;
  },

  /**
   * Get top/bottom venues by tips
   * @param {Object} filters - { from, to, limit, order }
   */
  async getTopVenues(filters = {}) {
    const params = new URLSearchParams();

    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/admin/analytics/top-venues?${params}`);
    return response.data;
  },

  /**
   * Get traction data: tip volume trends, top venues, venue activity
   * @param {Object} filters - { from, to }
   */
  async getTraction(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const response = await api.get(`/admin/analytics/traction?${params}`);
    return response.data;
  },

  /**
   * Get per-worker tip distribution for a venue and period
   * @param {string} venueId - Venue UUID
   * @param {Object} filters - { from, to }
   */
  async getVenueWorkerDistribution(venueId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const response = await api.get(`/admin/analytics/traction/venues/${venueId}/worker-distribution?${params}`);
    return response.data;
  },

  /**
   * Get revenue data: gross volume, estimated fee, confirmed invoices, run-rate
   * @param {Object} filters - { from, to }
   */
  async getRevenue(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const response = await api.get(`/admin/analytics/revenue?${params}`);
    return response.data;
  },

  /**
   * Get activation funnel data: tenants, venues, devices, worker invites, Stripe Connect
   * @param {Object} filters - { from, to }
   */
  async getActivation(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const response = await api.get(`/admin/analytics/activation?${params}`);
    return response.data;
  },

  /**
   * Get operations health data: device status, payment failures, worker issues
   * @param {Object} filters - { from, to }
   */
  async getOperations(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const response = await api.get(`/admin/analytics/operations?${params}`);
    return response.data;
  },

  /**
   * Get per-worker, per-period tip distribution aggregated from TipTransfer records.
   * @param {{ venueId?: string, tenantId?: string, workerDestination?: string, from?: string, to?: string, groupBy?: string }} params
   */
  async getTipDistribution(params = {}) {
    const p = new URLSearchParams();
    if (params.venueId) p.append('venueId', params.venueId);
    if (params.tenantId) p.append('tenantId', params.tenantId);
    if (params.workerDestination) p.append('workerDestination', params.workerDestination);
    if (params.from) p.append('from', params.from);
    if (params.to) p.append('to', params.to);
    if (params.groupBy) p.append('groupBy', params.groupBy);
    const response = await api.get(`/admin/analytics/tip-distribution?${p}`);
    return response.data;
  }
};

// ==========================================
// DEPRECATED: Assignment service removed
// Admin non dovrebbe creare/gestire assignments
// ==========================================

export const commercialOfferService = {
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.active !== undefined) query.append('active', params.active);
    if (params.page) query.append('page', params.page);
    if (params.limit) query.append('limit', params.limit);
    const response = await api.get(`/admin/commercial-offers?${query}`);
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/admin/commercial-offers/${id}`);
    return response.data;
  },

  async listLegacy(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/commercial-offers/legacy${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getLegacy(id) {
    const response = await api.get(`/admin/commercial-offers/legacy/${id}`);
    return response.data;
  },

  async getContractPreview(id) {
    const response = await api.get(`/admin/commercial-offers/${id}/contract-preview`);
    return response.data;
  },

  async getLegacyContractPreview(id) {
    const response = await api.get(`/admin/commercial-offers/legacy/${id}/contract-preview`);
    return response.data;
  },

  async previewContractDraft(data) {
    const response = await api.post('/admin/commercial-offers/contract-preview', data);
    return response.data;
  },

  async listStripePromotionCodes() {
    const response = await api.get('/admin/commercial-offers/stripe-promotion-codes');
    return response.data;
  },

  async create(data) {
    const response = await api.post('/admin/commercial-offers', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/admin/commercial-offers/${id}`, data);
    return response.data;
  },

  async setDefault(id) {
    const response = await api.put(`/admin/commercial-offers/${id}/set-default`);
    return response.data;
  },

  async checkDeletion(id) {
    const response = await api.get(`/admin/commercial-offers/${id}/deletion-check`);
    return response.data;
  },

  async deactivate(id) {
    const response = await api.delete(`/admin/commercial-offers/${id}`);
    return response.data;
  },

  async reactivate(id) {
    const response = await api.put(`/admin/commercial-offers/${id}/reactivate`);
    return response.data;
  },

  async hardDelete(id) {
    const response = await api.delete(`/admin/commercial-offers/${id}?mode=hard`);
    return response.data;
  },

  async addAccess(id, data) {
    const response = await api.post(`/admin/commercial-offers/${id}/access`, data);
    return response.data;
  },

  async removeAccess(id, accessId) {
    const response = await api.delete(`/admin/commercial-offers/${id}/access/${accessId}`);
    return response.data;
  },

  async createInvitation(id, data) {
    const response = await api.post(`/admin/commercial-offers/${id}/invitations`, data);
    return response.data;
  },

  async sendInvitationEmail(id, invitationId, data) {
    const response = await api.post(`/admin/commercial-offers/${id}/invitations/${invitationId}/send-email`, data);
    return response.data;
  },

  async deleteInvitation(id, invitationId) {
    const response = await api.delete(`/admin/commercial-offers/${id}/invitations/${invitationId}`);
    return response.data;
  },

  async searchUsers(q) {
    const response = await api.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
    return response.data;
  },

  async getSignedContracts(offerId) {
    const response = await api.get(`/admin/commercial-offers/${offerId}/signed-contracts`);
    return response.data;
  }
};

export const planService = {
  async list() {
    const response = await api.get('/admin/plans');
    return response.data;
  },

  async quickCreate(data) {
    const response = await api.post('/admin/plans/quick-create', data);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/admin/plans', data);
    return response.data;
  },

  async update(planId, data) {
    const response = await api.put(`/admin/plans/${planId}`, data);
    return response.data;
  },

  async deletePlan(planId) {
    const response = await api.delete(`/admin/plans/${planId}`);
    return response.data;
  },

  async startEdit(planId) {
    const response = await api.post(`/admin/plans/${planId}/start-edit`);
    return response.data;
  },

  async createVersion(planId, data = {}) {
    const response = await api.post(`/admin/plans/${planId}/versions`, data);
    return response.data;
  },

  async updateVersion(versionId, data) {
    const response = await api.put(`/admin/plans/versions/${versionId}`, data);
    return response.data;
  },

  async deleteVersion(versionId) {
    const response = await api.delete(`/admin/plans/versions/${versionId}`);
    return response.data;
  },

  async cloneVersion(versionId) {
    const response = await api.post(`/admin/plans/versions/${versionId}/clone`);
    return response.data;
  },

  async createPrice(versionId, data) {
    const response = await api.post(`/admin/plans/versions/${versionId}/prices`, data);
    return response.data;
  },

  async createPriceFromStripe(versionId, data) {
    const response = await api.post(`/admin/plans/versions/${versionId}/prices/from-stripe`, data);
    return response.data;
  },

  async importPricesFromProduct(versionId, stripeProductId, mode, targetRoles = [], stripePriceIds = []) {
    const response = await api.post(`/admin/plans/versions/${versionId}/prices/from-stripe-product`, { stripeProductId, mode, targetRoles, stripePriceIds });
    return response.data;
  },

  async listFeatureDefinitions() {
    const response = await api.get('/admin/plans/feature-definitions');
    return response.data;
  },

  async createFeatureDefinition(data) {
    const response = await api.post('/admin/plans/feature-definitions', data);
    return response.data;
  },

  async updateFeatureDefinition(id, data) {
    const response = await api.put(`/admin/plans/feature-definitions/${id}`, data);
    return response.data;
  },

  async deleteFeatureDefinition(id) {
    const response = await api.delete(`/admin/plans/feature-definitions/${id}`);
    return response.data;
  },

  async reorderFeatureDefinitions(order) {
    const response = await api.put('/admin/plans/feature-definitions/reorder', { order });
    return response.data;
  },

  async reorderPlans(order) {
    // order: [{ planId, displayOrder }, ...]
    const response = await api.put('/admin/plans/reorder', { order });
    return response.data;
  },

  async saveFeatures(versionId, features) {
    const response = await api.put(`/admin/plans/versions/${versionId}/features`, { features });
    return response.data;
  },

  async savePlanFeatures(planId, features) {
    const response = await api.put(`/admin/plans/${planId}/features`, { features });
    return response.data;
  },

  async updatePrice(priceId, data) {
    const response = await api.put(`/admin/plans/prices/${priceId}`, data);
    return response.data;
  },

  async createStripeMapping(priceId, data) {
    const response = await api.post(`/admin/plans/prices/${priceId}/stripe-mappings`, data);
    return response.data;
  },

  async listStripeProducts(search = '') {
    const response = await api.get('/admin/plans/stripe/products', { params: search ? { search } : {} });
    return response.data;
  },

  async verifyStripeMapping(mappingId) {
    const response = await api.post(`/admin/plans/stripe-mappings/${mappingId}/verify`);
    return response.data;
  },

  async deleteStripeMapping(mappingId) {
    const response = await api.delete(`/admin/plans/stripe-mappings/${mappingId}`);
    return response.data;
  }
};

export const auditLogService = {
  /**
   * Get paginated audit log entries with optional filters.
   * @param {object} params - { action, entityType, adminId, q, from, to, page, limit }
   */
  async getLogs(params = {}) {
    const query = new URLSearchParams();
    if (params.action) query.append('action', params.action);
    if (params.entityType) query.append('entityType', params.entityType);
    if (params.adminId) query.append('adminId', params.adminId);
    if (params.q) query.append('q', params.q);
    if (params.from) query.append('from', params.from);
    if (params.to) query.append('to', params.to);
    if (params.page) query.append('page', params.page);
    if (params.limit) query.append('limit', params.limit);
    const response = await api.get(`/admin/audit-log?${query.toString()}`);
    return response.data;
  },

  /**
   * Get list of distinct action strings (for filter dropdown).
   */
  async getActions() {
    const response = await api.get('/admin/audit-log/actions');
    return response.data;
  },
};

// ── Operational Alerts ────────────────────────────────────────────────────────

// ── Events (VenueEvent + DeviceEvent) ────────────────────────────────────────

export const eventsService = {
  async getVenueEvents(params = {}) {
    const q = new URLSearchParams();
    ['venueId', 'tenantId', 'eventType', 'sourceType', 'q', 'from', 'to', 'page', 'limit'].forEach(k => {
      if (params[k]) q.append(k, params[k]);
    });
    const res = await api.get(`/admin/events/venue?${q.toString()}`);
    return res.data;
  },
  async getVenueEventTypes() {
    const res = await api.get('/admin/events/venue/types');
    return res.data;
  },
  async getDeviceEvents(params = {}) {
    const q = new URLSearchParams();
    ['deviceId', 'venueId', 'tenantId', 'eventType', 'q', 'from', 'to', 'page', 'limit'].forEach(k => {
      if (params[k]) q.append(k, params[k]);
    });
    const res = await api.get(`/admin/events/device?${q.toString()}`);
    return res.data;
  },
  async getDeviceEventTypes() {
    const res = await api.get('/admin/events/device/types');
    return res.data;
  },
};

// ── Commercial Commissions ─────────────────────────────────────────────────────

export const commercialCommissionsService = {
  async getAgents(params = {}) {
    const query = new URLSearchParams();
    if (params.includeInactive) query.append('includeInactive', 'true');
    const response = await api.get(`/admin/commercial-commissions/agents?${query}`);
    return response.data;
  },

  async createAgent(data) {
    const response = await api.post('/admin/commercial-commissions/agents', data);
    return response.data;
  },

  async updateAgent(id, data) {
    const response = await api.patch(`/admin/commercial-commissions/agents/${id}`, data);
    return response.data;
  },

  async getRules(params = {}) {
    const query = new URLSearchParams();
    if (params.tenantId) query.append('tenantId', params.tenantId);
    if (params.commercialAgentId) query.append('commercialAgentId', params.commercialAgentId);
    if (params.activeOnly != null) query.append('activeOnly', String(params.activeOnly));
    const response = await api.get(`/admin/commercial-commissions/rules?${query}`);
    return response.data;
  },

  async upsertRule(data) {
    const response = await api.post('/admin/commercial-commissions/rules', data);
    return response.data;
  },

  async updateRule(id, data) {
    const response = await api.patch(`/admin/commercial-commissions/rules/${id}`, data);
    return response.data;
  },

  async deactivateRule(id) {
    const response = await api.patch(`/admin/commercial-commissions/rules/${id}/deactivate`);
    return response.data;
  },

  async getMonthly(params = {}) {
    const query = new URLSearchParams();
    if (params.month) query.append('month', params.month);
    if (params.tenantId) query.append('tenantId', params.tenantId);
    if (params.venueId) query.append('venueId', params.venueId);
    if (params.deviceId) query.append('deviceId', params.deviceId);
    if (params.commercialAgentId) query.append('commercialAgentId', params.commercialAgentId);
    if (params.status) query.append('status', params.status);
    const response = await api.get(`/admin/commercial-commissions/monthly?${query}`);
    return response.data;
  },

  async generateSnapshot(params = {}) {
    const response = await api.post('/admin/commercial-commissions/monthly/generate', params);
    return response.data;
  },

  async generateSnapshotRetroactive(params = {}) {
    const response = await api.post('/admin/commercial-commissions/monthly/generate-retroactive', params);
    return response.data;
  },

  async markAsPaid(id) {
    const response = await api.patch(`/admin/commercial-commissions/monthly/${id}/mark-paid`);
    return response.data;
  },

  async exportCsv(params = {}) {
    const query = new URLSearchParams();
    if (params.month) query.append('month', params.month);
    if (params.monthRange) query.append('monthRange', params.monthRange);
    if (params.tenantId) query.append('tenantId', params.tenantId);
    if (params.venueId) query.append('venueId', params.venueId);
    if (params.deviceId) query.append('deviceId', params.deviceId);
    if (params.commercialAgentId) query.append('commercialAgentId', params.commercialAgentId);
    if (params.status) query.append('status', params.status);
    const response = await api.get(`/admin/commercial-commissions/export?${query}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Seller Portal: account venditori, bonifici, saldi, referral
export const sellerPortalAdminService = {
  async getSellers() {
    const response = await api.get('/admin/commercial-commissions/sellers');
    return response.data;
  },

  async inviteSeller(agentId, data = {}) {
    const response = await api.post(`/admin/commercial-commissions/agents/${agentId}/seller-account`, data);
    return response.data;
  },

  async setSellerStatus(agentId, status) {
    const response = await api.patch(`/admin/commercial-commissions/agents/${agentId}/seller-account`, { status });
    return response.data;
  },

  async revokeSellerSessions(agentId) {
    const response = await api.post(`/admin/commercial-commissions/agents/${agentId}/seller-account/revoke-sessions`);
    return response.data;
  },

  async getPayouts(params = {}) {
    const query = new URLSearchParams();
    if (params.commercialAgentId) query.append('commercialAgentId', params.commercialAgentId);
    const response = await api.get(`/admin/commercial-commissions/payouts?${query}`);
    return response.data;
  },

  async createPayout(data) {
    const response = await api.post('/admin/commercial-commissions/payouts', data);
    return response.data;
  },

  async reversePayout(id, reason) {
    const response = await api.patch(`/admin/commercial-commissions/payouts/${id}/reverse`, { reason });
    return response.data;
  },

  async getReferrals() {
    const response = await api.get('/admin/commercial-commissions/referrals');
    return response.data;
  },
};

// ── Operational Alerts ────────────────────────────────────────────────────────

export const operationalAlertsService = {
  async getAlerts(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') query.append(k, v); });
    const response = await api.get(`/admin/operational-alerts?${query.toString()}`);
    return response.data;
  },

  async getAlert(id) {
    const response = await api.get(`/admin/operational-alerts/${id}`);
    return response.data;
  },

  async acknowledge(id) {
    const response = await api.patch(`/admin/operational-alerts/${id}/acknowledge`);
    return response.data;
  },

  async snooze(id, hours = 24) {
    const response = await api.patch(`/admin/operational-alerts/${id}/snooze`, { hours });
    return response.data;
  },

  async resolve(id, reason = '') {
    const response = await api.patch(`/admin/operational-alerts/${id}/resolve`, { reason });
    return response.data;
  },

  async evaluate() {
    const response = await api.post('/admin/operational-alerts/evaluate');
    return response.data;
  },
};

export const legacyTenantService = {
  async list() {
    const res = await api.get('/admin/legacy-tenants');
    return res.data;
  },
  async get(tenantId) {
    const res = await api.get(`/admin/legacy-tenants/${tenantId}`);
    return res.data;
  },
  async save(tenantId, { features, feePercent, feeFixedCents, notes, applyLegacyContractFee }) {
    const res = await api.put(`/admin/legacy-tenants/${tenantId}`, { features, feePercent, feeFixedCents, notes, applyLegacyContractFee });
    return res.data;
  },
  async saveBulk({ features, feePercent, feeFixedCents, notes, applyLegacyContractFee, scope, limit }) {
    const res = await api.put('/admin/legacy-tenants/bulk', {
      features,
      feePercent,
      feeFixedCents,
      notes,
      applyLegacyContractFee,
      scope,
      limit,
    });
    return res.data;
  },
  async deleteConfig(tenantId) {
    const res = await api.delete(`/admin/legacy-tenants/${tenantId}/features`);
    return res.data;
  },
};

export default api;
